/*
 * AdminPlatformFees — verqo's commission engine.
 *
 * Default 3% fee deducted from every dropship daily invoice. Admins can
 * raise, lower, or zero out the rate per seller (e.g. partner deals).
 * Buyers are NEVER charged a per-order platform fee.
 *
 * Sections:
 *   • Header: lifetime fees collected · invoice count · default %
 *   • Per-seller table: search, effective %, override action, dialog
 *   • Recent ledger: last 100 collected fees with seller/buyer attribution
 */
import { useEffect, useMemo, useState } from "react";
import { DollarSign, Search, Edit2, RotateCcw, X, Loader2, Receipt, TrendingUp } from "lucide-react";
import api from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { PageHeader } from "../../components/PortalShell";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../components/ui/dialog";
import { toast } from "sonner";

const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const pctStr = (p) => `${(Number(p) || 0).toFixed(p % 1 === 0 ? 0 : 2)}%`;

export default function AdminPlatformFees() {
  const [config, setConfig] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [edit, setEdit] = useState(null);  // { seller_id, name, current_pct, uses_default }
  const [editPct, setEditPct] = useState("");
  const [editNote, setEditNote] = useState("");
  const [saving, setSaving] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const [c, l] = await Promise.all([
        api.get("/admin/platform-fees/config").then(r => r.data),
        api.get("/admin/platform-fees/ledger?limit=100").then(r => r.data),
      ]);
      setConfig(c);
      setLedger(l.rows || []);
    } catch (e) {
      toast.error("Failed to load platform fees");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, []);

  const filtered = useMemo(() => {
    if (!config?.sellers) return [];
    const q = query.trim().toLowerCase();
    if (!q) return config.sellers;
    return config.sellers.filter(s =>
      (s.company || "").toLowerCase().includes(q) ||
      (s.name || "").toLowerCase().includes(q) ||
      (s.email || "").toLowerCase().includes(q)
    );
  }, [config, query]);

  const overrideCount = useMemo(
    () => (config?.sellers || []).filter(s => !s.uses_default).length,
    [config],
  );

  const openEdit = (seller) => {
    setEdit(seller);
    setEditPct(seller.uses_default ? "" : String(seller.override_pct ?? ""));
    setEditNote(seller.note || "");
  };
  const closeEdit = () => { setEdit(null); setEditPct(""); setEditNote(""); };

  const saveOverride = async () => {
    if (!edit) return;
    const val = editPct.trim() === "" ? null : Number(editPct);
    if (val !== null && (Number.isNaN(val) || val < 0 || val > 50)) {
      toast.error("Enter a number between 0 and 50, or leave blank for default");
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/admin/platform-fees/seller/${edit.seller_id}`, {
        pct: val, note: editNote.trim() || null,
      });
      toast.success(val === null
        ? `Reverted ${edit.company || edit.name} to default ${pctStr(config?.default_pct)}`
        : `${edit.company || edit.name} set to ${pctStr(val)}`);
      closeEdit();
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    } finally {
      setSaving(false);
    }
  };

  const clearOverride = async (seller) => {
    if (!window.confirm(`Reset ${seller.company || seller.name} to default ${pctStr(config?.default_pct)}?`)) return;
    try {
      await api.patch(`/admin/platform-fees/seller/${seller.seller_id}`, { pct: null });
      toast.success("Override cleared");
      refresh();
    } catch {
      toast.error("Failed to clear override");
    }
  };

  if (loading || !config) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-6 h-6 animate-spin text-foreground/40" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="admin-platform-fees">
      <PageHeader title="Platform Fees" subtitle="verqo's commission on dropship daily invoices · auto-deducted from seller payout" />

      {/* KPI strip */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <KpiCard
          icon={<DollarSign className="w-4 h-4" />}
          label="Default rate"
          value={pctStr(config.default_pct)}
          hint="Applied to every seller unless overridden"
          testid="kpi-default-pct"
        />
        <KpiCard
          icon={<Receipt className="w-4 h-4" />}
          label="Lifetime fees collected"
          value={money(config.lifetime_fee_collected)}
          hint={`${config.lifetime_invoice_count} paid invoices`}
          testid="kpi-lifetime-fee"
        />
        <KpiCard
          icon={<TrendingUp className="w-4 h-4" />}
          label="Sellers with override"
          value={String(overrideCount)}
          hint={`${(config.sellers || []).length} sellers total`}
          testid="kpi-override-count"
        />
        <KpiCard
          icon={<DollarSign className="w-4 h-4" />}
          label="Avg fee per invoice"
          value={config.lifetime_invoice_count ? money(config.lifetime_fee_collected / config.lifetime_invoice_count) : "—"}
          hint="Across all collected fees"
          testid="kpi-avg-fee"
        />
      </div>

      {/* Sellers table */}
      <div className="rounded-2xl border bg-background">
        <div className="flex items-center justify-between gap-3 p-4 border-b">
          <h3 className="text-sm font-semibold tracking-tight">Per-seller rate</h3>
          <div className="relative w-72">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-foreground/40" />
            <Input
              data-testid="seller-search"
              placeholder="Search by company, name, email…"
              className="pl-9 h-9"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-foreground/55 border-b">
                <th className="px-4 py-2.5 font-medium">Seller</th>
                <th className="px-4 py-2.5 font-medium">Email</th>
                <th className="px-4 py-2.5 font-medium">Effective rate</th>
                <th className="px-4 py-2.5 font-medium">Source</th>
                <th className="px-4 py-2.5 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-foreground/50">No sellers match.</td></tr>
              )}
              {filtered.map(s => (
                <tr key={s.seller_id} className="border-b last:border-0 hover:bg-foreground/3">
                  <td className="px-4 py-3">
                    <div className="font-medium" data-testid={`seller-row-${s.seller_id}`}>{s.company || s.name || "—"}</div>
                    {s.name && s.company && <div className="text-xs text-foreground/55">{s.name}</div>}
                  </td>
                  <td className="px-4 py-3 text-foreground/70">{s.email}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold ${
                      s.uses_default
                        ? "bg-foreground/8 text-foreground/70"
                        : "bg-emerald-100 text-emerald-700"
                    }`}>
                      {pctStr(s.effective_pct)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-foreground/65">
                    {s.uses_default ? (
                      <span>Platform default</span>
                    ) : (
                      <div>
                        <div className="font-medium text-emerald-700">Custom override</div>
                        {s.note && <div className="text-foreground/55 truncate max-w-[280px]">{s.note}</div>}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right space-x-2 whitespace-nowrap">
                    <Button
                      variant="outline" size="sm"
                      onClick={() => openEdit(s)}
                      data-testid={`edit-${s.seller_id}`}
                    >
                      <Edit2 className="w-3.5 h-3.5 mr-1.5" />
                      {s.uses_default ? "Override" : "Edit"}
                    </Button>
                    {!s.uses_default && (
                      <Button
                        variant="ghost" size="sm"
                        onClick={() => clearOverride(s)}
                        data-testid={`clear-${s.seller_id}`}
                      >
                        <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
                        Reset
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent ledger */}
      <div className="rounded-2xl border bg-background">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-sm font-semibold tracking-tight">Recent fee collections</h3>
          <span className="text-xs text-foreground/55">{ledger.length} most recent</span>
        </div>
        {ledger.length === 0 ? (
          <div className="p-8 text-center text-foreground/50 text-sm">
            No fees collected yet. Fees are recorded when dropship invoices are paid.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-foreground/55 border-b">
                  <th className="px-4 py-2.5 font-medium">Collected</th>
                  <th className="px-4 py-2.5 font-medium">Invoice</th>
                  <th className="px-4 py-2.5 font-medium">Seller</th>
                  <th className="px-4 py-2.5 font-medium">Buyer</th>
                  <th className="px-4 py-2.5 font-medium text-right">Gross</th>
                  <th className="px-4 py-2.5 font-medium text-right">Fee</th>
                  <th className="px-4 py-2.5 font-medium text-right">Net to seller</th>
                </tr>
              </thead>
              <tbody>
                {ledger.map(r => (
                  <tr key={r.id} className="border-b last:border-0 hover:bg-foreground/3">
                    <td className="px-4 py-2.5 text-xs text-foreground/65">
                      {r.collected_at ? new Date(r.collected_at).toLocaleString() : "—"}
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs">{r.invoice_code || r.invoice_id?.slice(0, 8)}</td>
                    <td className="px-4 py-2.5">{r.seller_name || r.seller_id?.slice(0, 8)}</td>
                    <td className="px-4 py-2.5 text-foreground/70">{r.buyer_name || r.buyer_id?.slice(0, 8)}</td>
                    <td className="px-4 py-2.5 text-right">{money(r.gross)}</td>
                    <td className="px-4 py-2.5 text-right">
                      <span className="font-medium text-emerald-700">{money(r.fee_amount)}</span>
                      <span className="text-xs text-foreground/55 ml-1">({pctStr(r.fee_pct)})</span>
                    </td>
                    <td className="px-4 py-2.5 text-right">{money(r.net_to_seller)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit dialog */}
      <Dialog open={!!edit} onOpenChange={(o) => !o && closeEdit()}>
        <DialogContent className="max-w-md" data-testid="override-dialog">
          <DialogHeader>
            <DialogTitle>Set platform fee for {edit?.company || edit?.name}</DialogTitle>
            <DialogDescription>
              Leave blank to revert to the platform default ({pctStr(config?.default_pct)}).
              Future invoices use the new rate; already-issued invoices keep their stored rate.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="pct">Custom rate (%)</Label>
              <Input
                id="pct" data-testid="override-pct"
                type="number" step="0.1" min="0" max="50"
                placeholder={`Default ${pctStr(config?.default_pct)}`}
                value={editPct} onChange={(e) => setEditPct(e.target.value)}
              />
              <div className="text-xs text-foreground/55 mt-1">
                Allowed range 0-50%. 0 = no fee for this seller.
              </div>
            </div>
            <div>
              <Label htmlFor="note">Note (optional)</Label>
              <Textarea
                id="note" data-testid="override-note"
                placeholder="e.g. Launch partner — flat 1% for first 6 months"
                value={editNote} onChange={(e) => setEditNote(e.target.value)}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={closeEdit}><X className="w-4 h-4 mr-1" />Cancel</Button>
            <Button onClick={saveOverride} disabled={saving} data-testid="save-override">
              {saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KpiCard({ icon, label, value, hint, testid }) {
  return (
    <div className="rounded-2xl border p-4 bg-background" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs text-foreground/55 uppercase tracking-wider">
        {icon}
        <span>{label}</span>
      </div>
      <div className="mt-1.5 text-2xl font-semibold tabular-nums">{value}</div>
      {hint && <div className="text-xs text-foreground/55 mt-0.5">{hint}</div>}
    </div>
  );
}
